interface ApiError {
  code: string;
  message: string;
  details?: any;
}

interface ApiResponse {
  success: boolean;
  error?: ApiError;
  data?: any;
}

export class ErrorHandler {
  static handle(error: any): string {
    // Handle network errors
    if (!error.response) {
      return 'Error de conexión. Verifique su conexión a internet.';
    }

    const response = error.response;
    
    // Handle API errors with our error structure
    if (response.data?.error?.code) {
      const apiError: ApiError = response.data.error;
      
      switch (apiError.code) {
        case 'VALIDATION_ERROR':
          return this.formatValidationError(apiError);
        case 'DOCUMENT_ERROR':
          return `Error en documento: ${apiError.message}`;
        case 'TRANSACTION_ERROR':
          return `Error de sistema: ${apiError.message}`;
        case 'FILE_ERROR':
          return `Error en archivo: ${apiError.message}`;
        case 'DUPLICATE_ERROR':
          return this.formatDuplicateError(apiError);
        case 'NOT_FOUND':
          return `Recurso no encontrado: ${apiError.message}`;
        default:
          return apiError.message || 'Error desconocido';
      }
    }
    
    // Handle direct error message from API
    if (response.data?.error?.message) {
      return response.data.error.message;
    }
    
    // Handle simple message format
    if (response.data?.message) {
      return response.data.message;
    }
    
    // Handle HTTP status codes
    switch (response.status) {
      case 400:
        return 'Datos inválidos. Por favor revise la información ingresada.';
      case 401:
        return 'Sesión expirada. Por favor inicie sesión nuevamente.';
      case 403:
        return 'No tiene permisos para realizar esta acción.';
      case 404:
        return 'El recurso solicitado no fue encontrado.';
      case 409:
        return 'El recurso ya existe. Verifique los datos únicos como RFC o email.';
      case 422:
        return 'Los datos proporcionados no son válidos.';
      case 500:
        return 'Error interno del servidor. Por favor intente más tarde.';
      default:
        return 'Error inesperado. Por favor intente nuevamente.';
    }
  }

  private static formatValidationError(apiError: ApiError): string {
    if (apiError.details?.field) {
      const fieldNames: Record<string, string> = {
        'rfc': 'RFC',
        'email': 'Email',
        'clabe': 'CLABE',
        'documents': 'Documentos',
        'beneficiaries': 'Beneficiarios',
        'phone': 'Teléfono',
        'firstName': 'Nombre',
        'lastName': 'Apellidos',
      };
      
      const fieldName = fieldNames[apiError.details.field] || apiError.details.field;
      return `Error en ${fieldName}: ${apiError.message}`;
    }
    
    return `Error de validación: ${apiError.message}`;
  }

  private static formatDuplicateError(apiError: ApiError): string {
    if (apiError.details?.field) {
      const fieldNames: Record<string, string> = {
        'rfc': 'RFC',
        'email': 'Email',
        'clabe': 'CLABE',
      };
      
      const fieldName = fieldNames[apiError.details.field] || apiError.details.field;
      return `Ya existe un registro con este ${fieldName}`;
    }
    
    return apiError.message;
  }

  static getErrorCode(error: any): string | null {
    return error.response?.data?.error?.code || null;
  }

  static getErrorDetails(error: any): any {
    return error.response?.data?.error?.details || null;
  }
}
